import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as signalR from "@microsoft/signalr";
import { eliminarMensaje, limpiarConversacion } from "../api/apiChatPrivado";

// Construir URL de SignalR correctamente
const getHubUrl = () => {
  const baseUrl = import.meta.env.VITE_API_QUINIELA || "/api";
  // Reemplazar /api con /hubs/chat-privado o use el protocolo correcto
  const apiBase = baseUrl.replace(/\/api\/?$/, "");
  return `${apiBase}/hubs/chat-privado`;
};

const HUB_URL = getHubUrl();

export const usePrivateChat = (token) => {
  const [conversations, setConversations] = useState([]);
  const [activeMessages, setActiveMessages] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [connected, setConnected] = useState(false);
  const connectionRef = useRef(null);
  // Ref para acceder al activeConversationId actual dentro del handler de SignalR
  const activeConversationIdRef = useRef(null);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (!token) return;

    console.log("[ChatPrivado] Conectando a:", HUB_URL);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => token,
        skipNegotiation: false,  // Necesitamos negociación para múltiples transports
        transport: signalR.HttpTransportType.WebSockets |
                   signalR.HttpTransportType.LongPolling,
        withCredentials: false,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connection.on("ReceivePrivateMessage", (payload) => {
      // payload: { id, emisorId, emisorNombre, receptorId, contenido, fechaEnvio, leido }
      const { emisorId, emisorNombre, contenido, fechaEnvio } = payload;
      const currentActiveId = activeConversationIdRef.current;

      // Si el mensaje es de la conversación activa, agregarlo a activeMessages
      if (emisorId === currentActiveId) {
        setActiveMessages((prev) => [...prev, payload]);
      }

      // Actualizar conversations: último mensaje, fecha y contador de no leídos
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.otroUsuarioId === emisorId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            ultimoMensaje: contenido,
            fechaUltimoMensaje: fechaEnvio,
            noLeidos:
              emisorId === currentActiveId
                ? updated[idx].noLeidos
                : updated[idx].noLeidos + 1,
          };
          return updated;
        } else {
          // Conversación nueva
          return [
            ...prev,
            {
              otroUsuarioId: emisorId,
              otroUsuarioNombre: emisorNombre,
              otroUsuarioPais: null,
              ultimoMensaje: contenido,
              fechaUltimoMensaje: fechaEnvio,
              noLeidos: emisorId === currentActiveId ? 0 : 1,
            },
          ];
        }
      });
    });

    connection.onreconnecting(() => {
      console.log("[ChatPrivado] Reconectando...");
      setConnected(false);
    });
    connection.onreconnected(() => {
      console.log("[ChatPrivado] Reconectado exitosamente");
      setConnected(true);
    });
    connection.onclose(() => {
      console.log("[ChatPrivado] Conexión cerrada");
      setConnected(false);
    });

    connection
      .start()
      .then(async () => {
        console.log("[ChatPrivado] Conectado exitosamente");
        setConnected(true);

        // NUEVO: Cargar conversaciones existentes al conectar
        try {
          const response = await fetch('/api/chat-privado/conversaciones', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setConversations(data);
            console.log("[ChatPrivado] Conversaciones cargadas:", data.length);
          }
        } catch (err) {
          console.warn("[ChatPrivado] Error cargando conversaciones:", err);
        }
      })
      .catch((err) => {
        // El transporte inicial podría fallar (ej: WebSocket) pero fallback a LongPolling es normal
        console.warn("[ChatPrivado] Intento inicial de conexión:", err.message);
      });

    connectionRef.current = connection;

    return () => {
      connection.stop();
    };
  }, [token]);

  const setActiveConversation = useCallback((userId) => {
    setActiveConversationId(userId);
    activeConversationIdRef.current = userId;
    // Resetear no leídos de esa conversación
    setConversations((prev) =>
      prev.map((c) =>
        c.otroUsuarioId === userId ? { ...c, noLeidos: 0 } : c
      )
    );
    // Limpiar mensajes activos (se recargarán vía REST desde el componente)
    setActiveMessages([]);
  }, []);

  const sendPrivateMessage = useCallback(async (receptorId, contenido) => {
    if (
      !connectionRef.current ||
      connectionRef.current.state !== signalR.HubConnectionState.Connected
    )
      return;
    if (!contenido?.trim()) return;

    // Agregar optimistamente a activeMessages
    const mensajeOptimista = {
      id: Date.now(),
      emisorId: null, // el propio usuario (no conocemos el id aquí)
      emisorNombre: null,
      receptorId,
      contenido: contenido.trim(),
      fechaEnvio: new Date().toISOString(),
      leido: false,
      _optimista: true,
    };
    setActiveMessages((prev) => [...prev, mensajeOptimista]);

    await connectionRef.current.invoke(
      "SendPrivateMessage",
      receptorId,
      contenido.trim()
    );
  }, []);

  const unreadTotal = useMemo(
    () => conversations.reduce((sum, c) => sum + c.noLeidos, 0),
    [conversations]
  );

  const deleteMessage = useCallback(async (mensajeId) => {
    const result = await eliminarMensaje(mensajeId);
    if (result.exito) {
      // Eliminar del estado local
      setActiveMessages((prev) => prev.filter((m) => m.id !== mensajeId));
    }
    return result;
  }, []);

  const clearConversation = useCallback(async (otroUsuarioId) => {
    const result = await limpiarConversacion(otroUsuarioId);
    if (result.exito) {
      // Limpiar los mensajes activos
      setActiveMessages([]);
      // Actualizar la lista de conversaciones
      setConversations((prev) =>
        prev.filter((c) => c.otroUsuarioId !== otroUsuarioId)
      );
    }
    return result;
  }, []);

  return {
    sendPrivateMessage,
    conversations,
    activeMessages,
    unreadTotal,
    setActiveConversation,
    connected,
    deleteMessage,
    clearConversation,
  };
};
